import { FC } from "react";
import { Alert, Box, CircularProgress } from "@mui/material";
import { Navigate, useLocation, useParams } from "react-router-dom";
import { useSpecificCounterparty } from "../../dataHooks/counterpartyQueries";
import { EntityMergerProvider } from "../../../../auth/entityMergerContext";
import { correctedEntityPath } from "../../../../utils/entityPaths";
import { SignOffPage } from "./signOffPage";

type Props = {};

type Params = {
  id?: string;
};

export const CounterPartySignOff: FC<Props> = () => {
  const { id } = useParams<Params>();
  const { pathname } = useLocation();

  const { data: counterparty, isLoading } = useSpecificCounterparty(id, {
    // Poll while on the sign-off page so a VP signing off picks up a rejection
    // or other signatures made by someone else without needing a manual reload.
    refetchInterval: 30000
  });

  if (isLoading) {
    return <CircularProgress size={24} />;
  }

  if (counterparty === undefined) {
    return (
      <Box sx={{ padding: 3 }}>
        <Alert severity="error">Counterparty not found</Alert>
      </Box>
    );
  }

  // Sign-off links in the mails point at /mergers for a merger; an older link may not. Only once
  // the loaded entity is the one in the URL (keepPreviousData, see the detail page).
  const correctedPath = correctedEntityPath(
    "counterparty",
    pathname,
    counterparty.isMerger
  );
  if (correctedPath && counterparty.id === id) {
    return <Navigate to={correctedPath} replace />;
  }

  return (
    <EntityMergerProvider value={Boolean(counterparty.isMerger)}>
      <Box flex={1}>
        <SignOffPage
          specificCounterparty={counterparty}
          key={counterparty.id}
        />
      </Box>
    </EntityMergerProvider>
  );
};
